import { Component, OnDestroy, OnInit, signal, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { Crop, CropVariety, getCrops } from '@next/arable-lib/crop';
import { filterLoaded } from '@next/arable-lib/external-resource';
import { AbstractDialogResultData, AbstractFormDialog, AbstractFormDialogData, FormDialogComponent } from '@next/core-lib/dialog';
import { Store } from '@ngrx/store';
import { debounceTime, filter, map, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { loadSoilTypesInitiate, SoilType, SoilTypeSelectors } from 'src/app/soil-type';

import { ManualCrop, Province, SiteState } from '../../../shared/models';
import { loadCropVarietiesByCropIdRequest } from '../../store/actions';
import { selectCropVarietiesByCropId, selectLastUsedProvince } from '../../store/selectors';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatOption, MatSelect } from '@angular/material/select';
import { AgrNumberDirective } from '@next/core-lib/form';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { AsyncPipe, KeyValuePipe } from '@angular/common';
import { TranslatePipe } from '@next/core-lib/i18n';

export interface ManualCropDialogInputData extends AbstractFormDialogData {
  manualCrop: ManualCrop;
}

export interface ManualCropDialogResultData {
  manualCrop: ManualCrop;
}

@Component({
    selector: 'app-manual-crop-form',
    templateUrl: './manual-crop-form.component.html',
    imports: [
        FormDialogComponent,
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatAutocompleteTrigger,
        MatAutocomplete,
        MatOption,
        AgrNumberDirective,
        MatError,
        MatSelect,
        MatSlideToggle,
        AsyncPipe,
        KeyValuePipe,
        TranslatePipe,
    ],
})
export class ManualCropFormComponent
  extends AbstractFormDialog<ManualCropDialogResultData>
  implements OnInit, OnDestroy {
  data = inject<ManualCropDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef = inject<MatDialogRef<ManualCropFormComponent, AbstractDialogResultData<ManualCropDialogResultData>>>(MatDialogRef);
  private readonly store = inject<Store<SiteState>>(Store);

  formGroup: FormGroup = new FormGroup({
    name: new FormControl('', [
      Validators.required,
      Validators.maxLength(100),
      Validators.pattern('^[a-zA-Z0-9\\s()\\-_,.]+$'),
    ]),
    province: new FormControl('', Validators.required),
    cropId: new FormControl('', Validators.required),
    cropVarietyId: new FormControl(''),
    areaInHectares: new FormControl('', [Validators.required, Validators.min(0.01)]),
    soilTypeId: new FormControl('', Validators.required),
    groundwaterPreservation: new FormControl(false),
    planetProof: new FormControl(false),
    organic: new FormControl(false),
  });

  eProvince = Province;
  soilTypes = signal<SoilType[]>([]);
  crops = signal<Crop[]>([]);
  cropVarieties: CropVariety[] = [];
  filteredCrops$ = this.formGroup.controls.cropId.valueChanges.pipe(
    startWith(''),
    filter((value) => typeof value === 'string'),
    map((value: string) => this.filterCrops(value, [...this.crops()])),
  );
  filteredCropVarieties$!: Observable<CropVariety[]>;
  destroy$ = new Subject<boolean>();

  constructor() {
    super();

    this.data.title = '';
  }

  ngOnInit(): void {
    this.selectCrops();
    this.subscribeToCropIdChanged();
    this.initForm();
    this.selectCropVarieties();
    this.selectLastUsedProvince();
    this.selectSoilTypes();
    this.store.dispatch(loadSoilTypesInitiate());

    if (this.isParticipatingToBem()) {
      this.formGroup.get('cropId')?.disable();
      this.formGroup.get('cropVarietyId')?.disable();
    }
  }

  initForm(): void {
    if (this.data.manualCrop) {
      this.formGroup.patchValue({
        name: this.data.manualCrop.name || '',
        province: this.data.manualCrop.province || '',
        cropId: this.data.manualCrop.cropId || '',
        cropVarietyId: this.data.manualCrop.cropVarietyId || '',
        areaInHectares: this.data.manualCrop.areaInHectares || '',
        soilTypeId: this.data.manualCrop.soilTypeId || '',
        groundwaterPreservation: this.data.manualCrop.groundwaterPreservation || false,
        planetProof: this.data.manualCrop.planetProof || false,
        organic: this.data.manualCrop.organic || false,
      });
    }
  }

  subscribeToCropIdChanged(): void {
    this.formGroup
      .get('cropId')
      ?.valueChanges.pipe(
        debounceTime(500),
        takeUntil(this.destroy$))
      .subscribe((cropId: number) => {
        if (cropId) {
          const crop = this.crops().find((c) => c.id === cropId);
          if (crop) {
            this.formGroup.get('name')?.setValue(crop.name);
            this.loadCropVarieties(cropId);
            this.formGroup.get('cropVarietyId')?.setValue(null);
          } else {
            this.formGroup.get('cropId')?.setValue(null, { emitModelToViewChange: false });
          }
        }
      });
  }

  selectSoilTypes(): void {
    this.store
      .select(SoilTypeSelectors.select)
      .pipe(takeUntil(this.destroy$), filterLoaded())
      .subscribe((soilTypes) => {
        this.soilTypes.set(soilTypes);
      });
  }

  selectCrops(): void {
    this.store
      .select(getCrops)
      .pipe(takeUntil(this.destroy$))
      .subscribe((crops) => {
        this.crops.set(crops);
      });
  }

  selectLastUsedProvince(): void {
    this.store
      .select(selectLastUsedProvince)
      .pipe(takeUntil(this.destroy$))
      .subscribe((lastUsedProvince) => {
        if (!this.data.manualCrop.province) {
          this.formGroup.get('province')!.setValue(lastUsedProvince);
        }
      });
  }

  selectCropVarieties(): void {
    this.store
      .select(selectCropVarietiesByCropId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((cropVarietiesByCropIds) => {
        const cropId = this.formGroup.get('cropId')?.value;
        this.cropVarieties = cropVarietiesByCropIds?.find((x) => x.cropId === cropId)?.cropVarieties ?? [];
        const varietyData = this.cropVarieties.filter((variety) => variety.id === this.data.manualCrop.cropVarietyId);
        if (this.data.manualCrop.cropVarietyId && !varietyData.length) {
          this.formGroup.get('cropVarietyId')?.setValue(null);
        } else {
          this.formGroup.get('cropVarietyId')?.setValue(this.data.manualCrop.cropVarietyId);
        }

        this.filteredCropVarieties$ = this.formGroup.controls.cropVarietyId.valueChanges.pipe(
          startWith(''),
          filter((value) => typeof value === 'string'),
          map((value: string) => this.filterCropVarieties(value, this.cropVarieties)),
        );
      });
  }

  save(): void {
    this.confirm({
      manualCrop: {
        ...this.formGroup.getRawValue(),
        id: this.data.manualCrop.id,
        cropName: this.crops().find((c) => c.id === this.formGroup.get('cropId')?.value)?.name,
        cropVarietyName: this.cropVarieties.find((c) => c.id === this.formGroup.get('cropVarietyId')?.value)?.name,
        soilTypeName: this.soilTypes().find((c) => c.id === this.formGroup.get('soilTypeId')?.value)?.name,
      },
    });
  }

  displayCrop(): (cropId?: number) => string {
    return (cropId?: number) => {
      if (!cropId) return '';
      return this.crops()?.find((crop) => crop.id === cropId)?.name ?? '';
    };
  }

  displayCropVariety() {
    return (id?: number) => this.cropVarieties?.find((cropVariety) => cropVariety.id === id)?.name ?? '';
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private loadCropVarieties(cropId: number) {
    if (cropId) {
      this.store.dispatch(loadCropVarietiesByCropIdRequest({ cropIds: [cropId] }));
    }
  }

  private filterCrops(value: string, items: Crop[]) {
    return [...items]
      .sort((a, b) => (!(a.name && b.name) ? 0 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      .filter((option) => (!option.name ? false : option.name.toLowerCase().includes(value.toLowerCase())));
  }

  private filterCropVarieties(value: string, items: CropVariety[]) {
    return [...items]
      .sort((a, b) => (!(a.name && b.name) ? 0 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      .filter((option) => (!option.name ? false : option.name.toLowerCase().includes(value.toLowerCase())));
  }

  isParticipatingToBem(): boolean {
    return this.data.manualCrop?.isParticipatingToBem === true;
  }
}
